import {
  DeleteForeverOutlined,
  EditOutlined,
  ExpandMore,
  ListAltOutlined,
  UploadFile,
} from "@mui/icons-material";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  TableCell,
  TableRow,
  IconButton,
  Collapse,
  Box,
  Tooltip,
  AccordionSummary,
  Accordion,
  AccordionDetails,
  Button,
} from "@mui/material";
import { Fragment, useState, useContext } from "react";

import { APPT_STATUS } from "../../../../globalConstants";
import DataContext from "../../../context/DataContext";
import useMedicalCompleteLock from "../../../hooks/useMedicalCompleteLock";
import { ClientDetailContext } from "../../cm/Dashboard/FullPages/ClientDetail/context/ClientDetailContext";
import HtmlTooltip from "../../cm/Dashboard/FullPages/ClientDetail/ProvApptDetails/components/HtmlToolTip";
import ProviderApptProceduresv2 from "../../cm/Dashboard/FullPages/ClientDetail/ProvApptDetails/ProviderApptProceduresv2";
import { MEDICALLY_COMPLETE_LOCK_MESSAGE } from "../ClientDetail/Case/utils/constants";

export function Row({
  caseInfo,
  handleApptDelete,
  handleProviderApptEdit,
  setProvider,
  setProviderDetailDisplay,
  handleUploadDocument,
  row,
}) {
  const { userRoles } = useContext(DataContext);
  const { setSelectedAppointment } = useContext(ClientDetailContext);
  const [open, setOpen] = useState(false);
  const { isAppointmentLocked } = useMedicalCompleteLock();
  const appointmentLocked = isAppointmentLocked(caseInfo);

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  function onClickProviderDetails(provider) {
    setProvider(provider);
    setProviderDetailDisplay(true);
  }

  return (
    <Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell align="left" component="th" scope="row">
          {row.provider.name}
        </TableCell>
        <TableCell align="left">{row?.providerType?.name}</TableCell>
        <TableCell align="left">
          {row.appointmentDate
            ? row.appointmentDate.toLocaleString("en-US", {
                dateStyle: "short",
              })
            : ""}
          &nbsp;
          {row.appointmentTime
            ? row.appointmentTime.toLocaleString("en-US", {
                timeStyle: "short",
              })
            : ""}
        </TableCell>
        <TableCell align="center">
          <div className="flex flex-row items-center gap-2">
            <div className="flex flex-row items-center gap-0">
              <HtmlTooltip
                title={
                  <>
                    <h1>{`Amount: ${
                      row.runningTotal ? formatter.format(row.runningTotal) : ""
                    }`}</h1>
                  </>
                }
                arrow
                className="arrow"
                placement="top-start"
              >
                <span>
                  {Number(row.runningTotal) > 0 && (
                    <AttachMoneyRoundedIcon className="text-bold visible text-[20px] text-purple-700" />
                  )}
                </span>
              </HtmlTooltip>
              <Box
                sx={{
                  borderRadius: "50%",
                  height: "15px",
                  width: "15px",
                  bgcolor:
                    row.status === "Done"
                      ? "#86CFAC"
                      : row.status === "Overdue"
                        ? "#FCA5A5"
                        : "#7DD3FC",
                }}
              />
            </div>
            {APPT_STATUS[row.status] ?? row.status ?? ""}
          </div>
        </TableCell>
        <TableCell align="center">{row.documentCount}</TableCell>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell className="pb-0 pt-0" colSpan={6}>
          <Collapse in={open} timeout="auto">
            <Box className="m-2">
              <div className="flex w-full justify-center p-2">
                <div className="flex w-full place-content-center justify-evenly text-center">
                  {userRoles.permissions.includes("provider.view_provider") ? (
                    <Tooltip title="Provider Details">
                      <Button
                        onClick={() => onClickProviderDetails(row.provider)}
                        className="flex rounded-md text-purple-600 hover:bg-purple-300 hover:text-purple-500"
                      >
                        <span>
                          <ListAltOutlined />
                        </span>
                      </Button>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                  {userRoles.permissions.includes("provider.change_appointment") ? (
                    <Tooltip
                      title={
                        appointmentLocked ? MEDICALLY_COMPLETE_LOCK_MESSAGE : "Edit Appointment"
                      }
                      placement="bottom"
                    >
                      <Button
                        className="rounded-md text-[#9333ea] hover:bg-purple-300 hover:text-white"
                        disabled={appointmentLocked}
                        onClick={() => handleProviderApptEdit(row)}
                      >
                        <span>
                          <EditOutlined />
                        </span>
                      </Button>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                  {userRoles.permissions.includes(
                    "provider.change_appointment",
                    "filemanager.view_document",
                    "filemanager.add_document"
                  ) && (
                    <Tooltip title="Upload Documents" placement="bottom">
                      <Button
                        className="rounded-md text-[#9333ea] hover:bg-purple-300 hover:text-white"
                        onClick={() => {
                          handleUploadDocument(row);
                          setSelectedAppointment(row);
                        }}
                      >
                        <UploadFile />
                      </Button>
                    </Tooltip>
                  )}
                  {userRoles.permissions.includes("provider.change_appointment") ? (
                    <Tooltip
                      title={
                        appointmentLocked ? MEDICALLY_COMPLETE_LOCK_MESSAGE : "Delete Appointment"
                      }
                      placement="bottom"
                    >
                      <Button
                        onClick={() => handleApptDelete(row)}
                        disabled={appointmentLocked}
                        className="rounded-md text-[#D32F2F] hover:bg-red-300 hover:text-white"
                      >
                        <span>
                          <DeleteForeverOutlined />
                        </span>
                      </Button>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                </div>
              </div>
              <Accordion className="m-1" disabled={!row.notes ? true : false}>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  {!row.notes ? "No Notes - Edit appointment info to add" : "Appointment Notes"}
                </AccordionSummary>
                <AccordionDetails>
                  <div className="bg-white">
                    <h3
                      className="notes"
                      dangerouslySetInnerHTML={{
                        __html: row.notes,
                      }}
                    ></h3>
                  </div>
                </AccordionDetails>
              </Accordion>
              <div className="m-1 flex w-full flex-col rounded-md border border-purple-600 p-2 text-center">
                {userRoles.permissions.includes("provider.view_administeredservices") ? (
                  <ProviderApptProceduresv2 selectedAppointment={row} caseInfo={caseInfo} />
                ) : (
                  ""
                )}
              </div>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}
